import React, { useEffect, useMemo, useState } from 'react';
import GenericDrawer from '@components/generic-drawer/GenericDrawer';
import { toast } from 'react-toastify';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { getInputs, initialValues } from './content';
import { useLazyQuery, useMutation } from '@apollo/client';
import Loader from '@components/loader/Loader';
import { isEmpty } from 'lodash';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { IMedicalPlanRatesDrawerProps } from '.';
import {
  createPlanSpecificMedical,
  fetchedClausesList,
  fetchedPolicyCoverList,
  getMedicalPlanRatesEnums,
  getPlanSpecificMedicalInfo,
  updatePlanSpecificMedical,
} from './queries';
import {
  LookupToList,
  extractClauses,
  extractPolicyCovers,
  graphqlToMedicalPlanRatesInfo,
} from './utils';
import { getError } from '@utils/graph-utils';
import { normaliseDynamicValues } from '@utils/dynamic-utils';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import DynamicForm from '@components/_old/dynamicForm/DynamicForm';
import { isZeroValue } from '@utils/validationUtils';
import _ from 'lodash';
import PrecisionService from '@services/PrecisionService';

const MedicalPlanRatesDrawer: React.FC<IMedicalPlanRatesDrawerProps> = ({
  open,
  onSuccess,
  onClose,
  planId,
  lineId,
  planCurrency,
  planCurrencySymbol,
  medicalPlanRateId,
}) => {
  const [booted, setBooted] = useState<boolean>(false);
  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();

  const [values, setValues] = useState<Record<string, any>>();

  const [lists, setLists] = useState<Record<string, Record<string, string>>>({
    policyCover: {},
    medicalPlanClasses: {},
    coNSSFOptions: {},
    medicalLevels: {},
    tpaApplicableOnOptions: {},
    clauseReference: {},
  });

  const [fetchedPolicyCoverListLazy] = useLazyQuery(fetchedPolicyCoverList());
  const [fetchedClausesLazy] = useLazyQuery(fetchedClausesList());
  const [medicalPlanRatesResultQuery] = useLazyQuery(
    getMedicalPlanRatesEnums()
  );
  const [medicalPlanRatesAction] = useMutation(
    medicalPlanRateId
      ? updatePlanSpecificMedical()
      : createPlanSpecificMedical()
  );
  const [medicalPlanRatesInfoQuery] = useLazyQuery(
    getPlanSpecificMedicalInfo()
  );

  const loadLovList = async () => {
    const data = await medicalPlanRatesResultQuery();
    const newMedicalPlanRatesEnums = LookupToList(data.data);
    const result: Record<string, Record<string, string>> = {
      medicalPlanClasses: newMedicalPlanRatesEnums['medicalPlanClasses'],
      coNSSFOptions: newMedicalPlanRatesEnums['coNSSFOptions'],
      tpaApplicableOnOptions:
        newMedicalPlanRatesEnums['tpaApplicableOnOptions'],
      medicalLevels: newMedicalPlanRatesEnums['medicalLevels'],
      policyCover: {},
      clauseReference: {},
    };

    return result;
  };

  const getEntityInfo = async () => {
    if (medicalPlanRateId) {
      const apiResult = await medicalPlanRatesInfoQuery({
        variables: { id: medicalPlanRateId },
      });
      if (apiResult.data) {
        const medicalPlanRateEntity = graphqlToMedicalPlanRatesInfo(
          apiResult.data
        );
        return medicalPlanRateEntity;
      }
    }

    return null;
  };

  const getPolicyCoverList = async (inputValue: string) => {
    if (inputValue !== null && inputValue.length >= 3) {
      const data = await fetchedPolicyCoverListLazy({
        variables: {
          selectedLineIds: [lineId],
          searchKeyword: inputValue,
        },
      });

      if (data.data) {
        const extractedFetchedPolicyCovers = extractPolicyCovers(data.data);

        return extractedFetchedPolicyCovers;
      }
    }

    return null;
  };

  const getClauseList = async (inputValue: any, newValues: any) => {
    if (inputValue !== null && inputValue.length >= 3) {
      const data = await fetchedClausesLazy({
        variables: {
          selectedLineIds: [lineId],
          searchKeyword: inputValue,
        },
      });

      if (data.data) {
        const extractedFetchedPolicyCovers = extractClauses(data.data);

        return extractedFetchedPolicyCovers;
      }
    }
    return null;
  };

  const initialize = async () => {
    try {
      const [medicalPlanRatesEntity, lovData] = await Promise.all([
        getEntityInfo(),
        loadLovList(),
      ]);

      let newValues = initialValues;

      if (medicalPlanRateId && medicalPlanRatesEntity) {
        if (
          medicalPlanRatesEntity.policyCover &&
          medicalPlanRatesEntity.policyCoverTitle
        ) {
          lovData.policyCover[medicalPlanRatesEntity.policyCover] =
            medicalPlanRatesEntity.policyCoverTitle;
        }

        if (
          medicalPlanRatesEntity.clauseReference &&
          medicalPlanRatesEntity.clauseReferenceTitle
        ) {
          lovData.clauseReference[medicalPlanRatesEntity.clauseReference] =
            medicalPlanRatesEntity.clauseReferenceTitle;
        }

        newValues = {
          planCurrency: planCurrency,
          policyCover: medicalPlanRatesEntity.policyCover,
          isMain: medicalPlanRatesEntity.isMain,
          isMandatory: medicalPlanRatesEntity.isMandatory,
          isEditable: medicalPlanRatesEntity.isEditable,
          nbOfAdherent: medicalPlanRatesEntity.nbOfAdherent,
          class: medicalPlanRatesEntity.class,
          ageFrom: medicalPlanRatesEntity.ageFrom,
          ageTo: medicalPlanRatesEntity.ageTo,
          coNssf: medicalPlanRatesEntity.coNssf,
          level: medicalPlanRatesEntity.level,
          sumInsured: medicalPlanRatesEntity.sumInsured,
          sumInsuredIsAdditive: medicalPlanRatesEntity.sumInsuredIsAdditive,
          netPremium: medicalPlanRatesEntity.netPremium,
          tpaType: medicalPlanRatesEntity.tpaType || '',
          tpaAmount: medicalPlanRatesEntity.tpaAmount || '',
          tpaFeesPercentage: medicalPlanRatesEntity.tpaFeesPercentage,
          tpaFeesApplicableOn: medicalPlanRatesEntity.tpaFeesApplicableOn,
          minTPAFees: medicalPlanRatesEntity.minTPAFees,
          maxTPAFees: medicalPlanRatesEntity.maxTPAFees,
          excessOnClaimType: medicalPlanRatesEntity.excessOnClaimType,
          excessOnClaimAmount: medicalPlanRatesEntity.excessOnClaimAmount,
          excessOnClaimPercentage:
            medicalPlanRatesEntity.excessOnClaimPercentage,
          excessOnClaimDays: medicalPlanRatesEntity.excessOnClaimDays,
          clauseReference: medicalPlanRatesEntity.clauseReference,
        };
      }

      setValues(newValues);
      setLists(lovData);
      setBooted(true);
    } catch (err) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  const onCustomChange = async (
    fieldName: string,
    value: any,
    allValues: Record<string, any>
  ) => {
    setValues(allValues);
  };

  const onSearch = async (fieldName: string, value: string) => {
    const newLovs = _.cloneDeep(lists);
    if (fieldName === 'policyCover') {
      newLovs.policyCover =
        (await getPolicyCoverList(value)) || lists.policyCover;
    } else if (fieldName === 'clauseReference') {
      await getClauseList(value, {});
      newLovs.clauseReference =
        (await getClauseList(value, {})) || lists.clauseReference;
    }
    setLists({
      ...lists,
      policyCover: newLovs.policyCover,
      clauseReference: newLovs.clauseReference,
    });
  };

  const submitForm = async (values: Record<string, any>) => {
    const [data] = normaliseDynamicValues(
      getInputs(undefined, lists, planCurrency, planCurrencySymbol),
      values
    );

    setFormDisabled(true);
    setSubmitButtonState('loading');

    try {
      const variablesMutation = {
        planSpecificMedicalInputs: {
          planID: planId,
          ageFrom: Number(data?.ageFrom),
          ageTo: Number(data?.ageTo),
          class: data?.class,
          coNssf: data?.coNssf,
          nbOfAdherent: isZeroValue(data?.nbOfAdherent)
            ? 0
            : Number(data?.nbOfAdherent) || null,
          policyCoverID: values.policyCover || '',
          netPremium: Number(data?.netPremium),
          sumInsured: Number(data?.sumInsured),
          sumInsuredIsAdditive: data?.sumInsuredIsAdditive,
          isMain: data?.isMain,
          isMandatory: data?.isMandatory,
          isEditable: data?.isEditable,
          level: data?.level || '',
          excessOnClaimType: data?.excessOnClaimType,
          excessOnClaimAmount: Number(data?.excessOnClaimAmount),
          excessOnClaimPercentage: PrecisionService.divideBy100(
            data?.excessOnClaimPercentage
          ),
          excessOnClaimDays: Number(data?.excessOnClaimDays),
          clauseID: data?.clauseReference || null,
          tpaType: data?.tpaType,
          tpaAmount:
            data?.tpaType === 'AMOUNT' ? Number(data?.tpaAmount) : null,
          tpaFeesPercentage:
            data?.tpaType === 'PERCENTAGE'
              ? PrecisionService.divideBy100(data?.tpaFeesPercentage)
              : null,
          tpaApplicableOn:
            data?.tpaType === 'PERCENTAGE' ? data?.tpaFeesApplicableOn : null,
          minTPAFees:
            data?.tpaType === 'PERCENTAGE' ? Number(data?.minTPAFees) : null,
          maxTPAFees:
            data?.tpaType === 'PERCENTAGE' ? Number(data?.maxTPAFees) : null,
        },
      };

      const res = await medicalPlanRatesAction({
        variables: medicalPlanRateId
          ? {
              ...variablesMutation,
              entityId: medicalPlanRateId,
            }
          : variablesMutation,
        errorPolicy: 'all',
      });

      if (isEmpty(res.errors)) {
        toast.success(
          <ToastSuccessMessage>
            {medicalPlanRateId
              ? 'Plan Rate successfully updated.'
              : 'Plan Rate successfully added.'}
          </ToastSuccessMessage>
        );
        setTimeout(() => {
          setSubmitButtonState('success');
          setFormDisabled(false);
          onSuccess();
          onClose();
        }, 500);
      } else {
        setSubmitButtonState(undefined);
        toast.error(<ToastErrorMessage>{getError(res)}</ToastErrorMessage>);
        setFormDisabled(false);
      }
    } catch (err) {
      setSubmitButtonState(undefined);
      toast.error(<ToastErrorMessage>{getError(err)}</ToastErrorMessage>);
      setFormDisabled(false);
    }
  };

  const inputForm = useMemo(() => {
    const result = getInputs(
      values || (initialValues as any),
      lists,
      planCurrency,
      planCurrencySymbol
    );
    return result;
  }, [values, lists]);

  const handleAgeDifference = (ageTo: any, ageFrom: any) => {
    const fromAge = Number(ageFrom);
    const toAge = Number(ageTo);
    const isAgeInvalid = fromAge != 0 && toAge != 0 && fromAge > toAge;

    if (isAgeInvalid) {
      return 'Age To must be greater than Age From';
    } else {
      return '';
    }
  };

  const handleTPAFeesDifference = (maxTPAFees: any, minTPAFees: any) => {
    const tPAFeesMin = Number(minTPAFees);
    const tPAFeesMax = Number(maxTPAFees);
    const isTPAInvalid =
      tPAFeesMin != 0 && tPAFeesMax != 0 && tPAFeesMin > tPAFeesMax;

    if (isTPAInvalid) {
      return 'Max TPA Fees must be greater than Min TPA Fees';
    } else {
      return '';
    }
  };

  const onCustomValidate = async (
    fieldName: string,
    values: Record<string, any>
  ) => {
    if (fieldName === 'ageFrom' || fieldName === 'ageTo') {
      return handleAgeDifference(values.ageTo, values.ageFrom);
    }
    if (fieldName === 'minTPAFees' || fieldName === 'maxTPAFees') {
      return handleTPAFeesDifference(values.maxTPAFees, values.minTPAFees);
    }

    return '';
  };

  const onCustomBlur = async (
    fieldName: string,
    values: Record<string, any>,
    errors: Record<string, any>
  ) => {
    if (fieldName === 'ageFrom' || fieldName === 'ageTo') {
      const ageToError = await onCustomValidate('ageTo', values);
      errors.ageTo = ageToError;
    }
    if (fieldName === 'minTPAFees' || fieldName === 'maxTPAFees') {
      const maxTPAFeesError = await onCustomValidate('maxTPAFees', values);
      errors.maxTPAFees = maxTPAFeesError;
    }
    return { values, errors };
  };

  return (
    <GenericDrawer
      title={medicalPlanRateId ? 'Modify Plan Rate' : 'Add Plan Rate'}
      onClose={() => onClose()}
      isOpen={open}
    >
      {!booted ? (
        <Loader />
      ) : (
        <>
          <DynamicForm
            inputs={inputForm}
            onSubmit={(values) => submitForm(values)}
            buttonText={'Submit'}
            submitButtonState={submitButtonState}
            isSubmitButtonDisabled={!!submitButtonState}
            disableForm={formDisabled}
            title="Information"
            hasDoprdownSpecificBehavior={true}
            onChange={onCustomChange}
            onCustomBlur={onCustomBlur}
            onSearch={async (f, v) => {
              await onSearch(f, v);
            }}
          />
        </>
      )}
    </GenericDrawer>
  );
};

export default MedicalPlanRatesDrawer;
